// §tenant-branded intake (FE PRD tenant-branded intake §1): an opaque tenant
// `intakeKey` arrives on any route as `?t=<key>`. We capture it once and persist
// it for the session so it survives the multi-step flow + refreshes, then thread
// it onto branding/catalog/submit calls. No key → default Fitpal intake (tenant 1).
import { DASHBOARD_LOGIN_URL } from '@/lib/utils';

const TENANT_KEY_STORAGE = 'fitpal_tenant_key';
// Tenant-scoped dashboard/login URL (patients complete follow-ups in the
// dashboard). Set from GET /tenant/config's dashboardUrl; read by the "Sign in"
// / "Complete Follow-Up" / "Go to my dashboard" links. Falls back to Fitpal's
// default dashboard when there's no tenant (or the BE hasn't supplied one yet).
const TENANT_DASHBOARD_URL_STORAGE = 'fitpal_tenant_dashboard_url';

export function setTenantDashboardUrl(url) {
  if (typeof window === 'undefined' || !url) return;
  try {
    sessionStorage.setItem(TENANT_DASHBOARD_URL_STORAGE, url);
  } catch (e) { /* storage unavailable */ }
}

export function getTenantDashboardUrl() {
  if (typeof window === 'undefined') return DASHBOARD_LOGIN_URL;
  try {
    return sessionStorage.getItem(TENANT_DASHBOARD_URL_STORAGE) || DASHBOARD_LOGIN_URL;
  } catch (e) {
    return DASHBOARD_LOGIN_URL;
  }
}

// Call once at app boot (synchronously, before first render — see index.js). If
// the URL carries ?t=, persist it. If it does NOT, this is an intentional
// plain/default intake, so clear any stale key from a prior tenant session in
// the same tab. Route navigation preserves ?t= via buildTenantUrl(), so a
// branded session's URL always carries the key — a missing ?t= on a full page
// load genuinely means "default Fitpal". Returns the effective key or null.
export function captureTenantKey() {
  if (typeof window === 'undefined') return null;
  try {
    const t = new URLSearchParams(window.location.search).get('t');
    if (t) {
      sessionStorage.setItem(TENANT_KEY_STORAGE, t);
      return t;
    }
    clearTenantKey();
    return null;
  } catch (e) {
    return null;
  }
}

// Remove the persisted tenant key (plain visit, or invalid key).
export function clearTenantKey() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(TENANT_KEY_STORAGE);
    sessionStorage.removeItem(TENANT_DASHBOARD_URL_STORAGE);
  } catch (e) {
    // storage unavailable (private mode) — default Fitpal still works
  }
}

// Preserve the opaque tenant key across SPA route changes + full-page links so
// the URL always carries ?t= (refresh-safe: a reloaded branded route still
// resolves the tenant instead of clearing it). No key → path unchanged.
export function buildTenantUrl(path, key = getTenantKey()) {
  if (!key) return path;
  try {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('t', key);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (e) {
    return path;
  }
}

// Read the persisted key (used by api.js to thread onto requests).
export function getTenantKey() {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(TENANT_KEY_STORAGE) || null;
  } catch (e) {
    return null;
  }
}

// §branding safety: tenant branding values come from a (semi-trusted) tenant
// admin via the BE and are rendered into <img src>/<link href>/an injected
// <style>. Validate before use.

// Only accept absolute https URLs for logo/favicon (reject http/data:/javascript:).
export function safeHttpsUrl(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' ? parsed.toString() : null;
  } catch (e) {
    return null;
  }
}

// Only accept a strict #rrggbb color. primaryColor is interpolated into the
// injected brand stylesheet, so anything else could break out of the CSS
// context — reject to the Fitpal default.
export function safePrimaryColor(raw, fallback = '#2d8a5f') {
  if (typeof raw !== 'string') return fallback;
  return /^#[0-9a-fA-F]{6}$/.test(raw.trim()) ? raw.trim() : fallback;
}
